import React from "react";
import { Button } from "react-bootstrap";
import { useAuth } from "../../../../auth/useAuth";

import FormBuilderSection from "./FormBuilderSection";
import "./FormBuilder.scss";

import ApiKeyContext from "../../../../contexts/ApiKeyContext";
import ApiDrivenModel from "../../../../models/ApiDrivenModel";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

export default function FormBuilder({ form, values = {}, className, onChange, showWarnings, hideTitle, onSubmit }) {
	const { currentUser } = useAuth();
	const ref = React.useRef();

	// Create copy of the form
	form = form && Object.keys(form).length ? JSON.parse(JSON.stringify(form)) : { sections: [] };

	const [currentValues, setCurrentValues] = React.useState(JSON.parse(JSON.stringify(values)));
	const [currentSection, setCurrentSection] = React.useState(0);
	const [valid, setValid] = React.useState(false);
	const [isSubmitted, setIsSubmitted] = React.useState(false);
	const [response, setResponse] = React.useState(false);
	const [validSections, setValidSections] = React.useState([]);
	const [showWarningsState, setShowWarnings] = React.useState(showWarnings);

	const API_KEY_CONTEXT = React.useContext(ApiKeyContext);
	const Dialog = withReactContent(Swal);


	// Compile input
	const inputs = form.sections.map(section => section.inputs).flat();

	const isEmailValid = value => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
	const invalidRequired = [undefined, null, ''];
	const isInvalid = input => {
		const currentValue = currentValues[input.id];
		if (!input.is_required) {
			return false;
		}
		if (input.is_required && invalidRequired.includes(currentValue)) {
			return input;
		}
		if ((input.type === 'multi-select') && (!Array.isArray(currentValue) || currentValue.length === 0)) {
			return input;
		}
		if ((input.type === 'email') && !isEmailValid(currentValue)) {
			return input;
		}
		return false;
	};
	const checkValidity = () => !inputs.find(isInvalid);

	// Apply passed in values
	form.sections.forEach(section => {
		section.inputs.forEach(input => {
			const value = (values[input.id] !== undefined) ? values[input.id] : input.value;
			input.value = value;
		});
	});

	const organizeValues = values => {
		const organized = {};
		form.sections.forEach(section => {
			section.inputs.forEach(input => {
				const value = values[input.id];
				if (input.is_system) {
					organized[input.id] = value;
				} else {
					const bucket = section.bucket || form.bucket;
					organized[bucket] = organized[bucket] || {};
					organized[bucket][input.id] = value;
				}
			})
		});
		return organized;
	}

	const updateSectionsValidity = () => {
		const newValidSections = form.sections.map(section => {
			return section.inputs.every(input => !isInvalid(input));
		});
		setValidSections(newValidSections);
	};

	const changeHandler = (field, value) => {
		currentValues[field] = value;
		const newOrganizedValues = organizeValues(currentValues);

		setCurrentValues(Object.assign({}, currentValues));

		// Use temporary variable, otherwise state ones do not change right
		const is_valid = checkValidity();
		updateSectionsValidity();
		setValid(is_valid);

		onChange && onChange({
			valid: is_valid,
			values: currentValues,
			organizedValues: newOrganizedValues
		});

		return true;
	};

	// Initialize
	React.useEffect(() => {
		if (form.js) {
			setTimeout(() => {
				new Function("values", "onChange", form.js)
					.call(form, currentValues, changeHandler);
			}, 50);
		}

		form.sections.forEach(section => {
			section.inputs.forEach(input => {
				if (input.value) {
					currentValues[input.id] = input.value;
				}
			});
		});
	}, []);

	const currentSectionValid = validSections[currentSection] || false;
	const goto = index => {
		setShowWarnings(false);
		setCurrentSection(index);
		ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};
	const goNext = () => {
		if (!currentSectionValid) {
			setShowWarnings(true);
			return;
		}
		goto(currentSection + 1);
	};
	const goBack = () => goto(Math.max(currentSection - 1, 0));
	const submit = e => {
		e.preventDefault();
		if (!valid) {
			setShowWarnings(true);
			return;
		}

		const pathValues = {};
		Object.keys(currentValues).forEach(key => {
			const value = currentValues[key];
			key = key.split('.');
			let pointer = pathValues;
			while (key.length > 1) {
				const current = key.shift();
				pointer[current] = pointer[current] || {};
				pointer = pointer[current];
			}
			pointer[key[0]] = value;
		});
		
		if (onSubmit) {
			onSubmit(pathValues);
			return;
		}

		// endpoint is the object name; e.g. Item_Inquiry
		if (form.endpoint) {
			const endpoint = form.endpoint.startsWith("/") ? form.endpoint : `/${form.endpoint}`;
			ApiDrivenModel.Request(API_KEY_CONTEXT, pathValues, { method: 'POST', endpoint })
				.then(response => {
					setIsSubmitted(true);
					setResponse(response.data);
				})
				.catch(err => {
					const message = err.response?.data.errors?.map(e => e.descr);
					Dialog.fire({ text: message || err.message || "Error submitting form", icon: "error", timerProgressBar: true, timer: 5000 });
				});
		}
	};

	const sectionCount = form.sections.length;
	const actions = [
		sectionCount > 1
		&& (<Button key="previous" variant="outline" onClick={goBack} className={!currentSection && 'disabled'}>Previous</Button>),
		currentSection < sectionCount - 1
		&& (<Button key="next" variant="primary" onClick={goNext} className={!currentSectionValid && 'disabled'}>Next</Button>),
		currentSection === sectionCount - 1
		&& <Button key="submit" variant="primary" className={!currentSectionValid && 'disabled'} onClick={submit}>{form.submit_label || "Submit"}</Button>
	].filter(Boolean);

	if (isSubmitted) {
		let message = form?.success_message || 'Submitted successfully';
		const matches = message.match(/{{=\s*(([\w\.]+))\s*}}/g);
		matches && matches.forEach(match => {
			const variable = match.substring(3).trim().slice(0, -2);
			let variableValue = response;
			const path = variable.split('.');
			while (path.length && variableValue) {
				const current = path.shift();
				variableValue = variableValue[current];
			}
			message = message.replace(match, variableValue || '');
		});

		return (
			<form className={"form-builder " + className + " response"}>
				<div className="form-header">
					<h1 dangerouslySetInnerHTML={{ __html: form?.title }} />
				</div>
				<div className="form-body">
					<h4 dangerouslySetInnerHTML={{ __html: message }} />
				</div>
			</form>
		);
	}
	const hasAfterSubmit = !!onSubmit || !!form.endpoint;
	const hasValidForm = !!form.sections?.length && hasAfterSubmit;
	
	return (
		<>
			{hasValidForm && (
				<form
					className={[
						"form-builder",
						className,
						valid ? 'valid' : 'invalid',
						showWarningsState ? 'show-warnings' : ''
					].filter(Boolean).join(' ')}
					data-section-count={sectionCount}
					ref={ref}
				>
					<div className="form-header">
						{!hideTitle && (<h1 dangerouslySetInnerHTML={{ __html: form?.title }} />)}
						<p className="instructions" dangerouslySetInnerHTML={{ __html: form?.instructions }} />
					</div >
					<div className="form-body">
						{
							form.sections.map((section, i) =>
								<FormBuilderSection
									key={i}
									className={i === currentSection ? 'active' : ''}
									section={section}
									values={values}
									onChange={changeHandler} />
							)
						}
					</div>
					<div className="form-footer">
						<div className="form-actions">
							{actions}
						</div>
						{sectionCount > 1 && (<div className="section-overview"> Step {currentSection + 1} of {sectionCount} </div>)}
						{currentUser && (<div className="user">Submitted by {currentUser.given_name} {currentUser.family_name}</div>)}
					</div>
					<style>
						{form.css}
					</style>
				</form>
			)}

			{!hasValidForm && (
				<>
					<pre>No Form Found</pre>
				</>
			)}
		</>
	);
}
